import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { apiGetProjects, apiDeleteProject, apiUpdateProject } from '~/lib/api/projects';
import { apiGetSubscription, type BillingSubscription } from '~/lib/api/billing';
import { $currentUser } from '~/lib/stores/auth';
import { ConfirmationDialog } from '~/components/ui/Dialog';
import ProjectList from '~/components/projects/ProjectList';
import NewProjectDialog from '~/components/projects/NewProjectDialog';
import type { ProjectListItem } from '@shared/types/project';
import { formatCredits } from '~/lib/plans-model';

type View = 'grid' | 'list';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useStore($currentUser);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProjectListItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState<View>('grid');

  const { data: projects = [], isLoading } = useQuery<ProjectListItem[]>({
    queryKey: ['projects'],
    queryFn: apiGetProjects,
  });

  const { data: subscription } = useQuery<BillingSubscription>({
    queryKey: ['subscription'],
    queryFn: apiGetSubscription,
  });

  const deleteMutation = useMutation({
    mutationFn: apiDeleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Project deleted');
      setDeleteTarget(null);
    },
    onError: () => {
      toast.error("Couldn't delete that project.");
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => apiUpdateProject(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
    onError: () => {
      toast.error("Couldn't rename that project.");
    },
  });

  const filteredProjects = useMemo(() => {
    if (!searchQuery.trim()) {
      return projects;
    }

    const q = searchQuery.toLowerCase();

    return projects.filter((p) => p.name.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q));
  }, [projects, searchQuery]);

  const handleDelete = useCallback(
    (id: string) => {
      const project = projects.find((p) => p.id === id);

      if (project) {
        setDeleteTarget(project);
      }
    },
    [projects],
  );

  const handleClick = useCallback(
    (id: string) => {
      navigate(`/project/${id}`);
    },
    [navigate],
  );

  const handleRename = useCallback(
    (id: string, name: string) => {
      renameMutation.mutate({ id, name });
    },
    [renameMutation],
  );

  const userName = user?.displayName || user?.email?.split('@')[0] || 'you';
  const initial = (user?.displayName || user?.email || '?')[0].toUpperCase();
  const projectCount = projects.length;
  const publicCount = projects.filter((p) => p.isPublic).length;

  return (
    <div className="ui-root min-h-dvh bg-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-30 backdrop-blur-[14px] bg-page/70 border-b border-hairline">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-7 py-3 flex flex-wrap items-center gap-x-3 gap-y-2.5 sm:gap-4.5">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img src="/dunea-mark.svg" width={22} height={22} alt="" style={{ display: 'block' }} />
            <span className="ui-brand text-[18px]">Dunea</span>
          </div>

          {/* Nav — only render routes that exist */}
          <nav className="hidden md:flex gap-0.5 ml-3.5">
            <button
              onClick={() => navigate('/projects')}
              className="px-3.5 py-1.5 rounded-full text-[12.5px] text-primary bg-white/[0.05]"
            >
              Projects
            </button>
          </nav>

          <div className="flex-1" />

          {/* Search — its own full-width row on phones */}
          <div className="relative order-last w-full sm:order-none sm:w-[200px] md:w-[280px]">
            <span className="i-ph:magnifying-glass absolute left-2.5 top-1/2 -translate-y-1/2 text-muted text-sm" />
            <input
              type="search"
              placeholder="Search projects"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="ui-input bg-raised pl-7.5"
            />
          </div>

          {/* New project */}
          <button
            onClick={() => setShowNewDialog(true)}
            className="ui-btn-accent whitespace-nowrap"
            aria-label="New project"
          >
            <span className="i-ph:plus text-sm" />
            <span className="hidden sm:inline">New project</span>
          </button>

          {/* Avatar */}
          <div className="w-7.5 h-7.5 rounded-full bg-raised border border-subtle flex items-center justify-center overflow-hidden flex-shrink-0">
            {user?.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-[11px] font-medium text-secondary">{initial}</span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-4 sm:px-7 pt-7 sm:pt-10 pb-20">
        {/* Page intro */}
        <div className="flex items-end gap-3.5 mb-8 flex-wrap">
          <div className="min-w-0">
            <span className="ui-caption">Workspace · {userName}</span>
            <h1 className="font-instrument text-[32px] sm:text-[44px] mt-2 leading-[1.05] tracking-[-0.02em] break-words">
              {projectCount === 0 ? (
                <>
                  No projects <i className="text-accent">yet.</i>
                </>
              ) : (
                <>
                  {projectCount} {projectCount === 1 ? 'project' : 'projects'}
                  {publicCount > 0 ? (
                    <>
                      , <i className="text-accent">{publicCount} public.</i>
                    </>
                  ) : (
                    '.'
                  )}
                </>
              )}
            </h1>
          </div>
          <div className="flex-1" />
          {subscription && (
            <div className="flex gap-4.5 items-end text-secondary">
              <div>
                <div className="ui-caption">Credits</div>
                <div className="font-instrument text-[24px] tracking-[-0.02em] text-primary mt-1">
                  {formatCredits(subscription.creditsRemaining)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filter + view row */}
        <div className="flex gap-2 items-center mb-4.5 flex-wrap">
          <span className="ui-chip border-prominent text-primary bg-sunken">
            All <span className="text-faint ml-1">{projectCount}</span>
          </span>
          <div className="flex-1" />
          <div className="ui-segmented">
            <button
              onClick={() => setView('grid')}
              className={`ui-segmented-btn inline-flex items-center gap-1.5 ${
                view === 'grid' ? 'ui-segmented-btn-active' : ''
              }`}
            >
              <span className="i-ph:grid-four text-sm" />
              Grid
            </button>
            <button
              onClick={() => setView('list')}
              className={`ui-segmented-btn inline-flex items-center gap-1.5 ${
                view === 'list' ? 'ui-segmented-btn-active' : ''
              }`}
            >
              <span className="i-ph:list text-sm" />
              List
            </button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="text-center py-20 text-secondary">Loading projects…</div>
        ) : filteredProjects.length === 0 ? (
          projects.length === 0 ? (
            // True empty state
            <div
              className="bg-surface border border-dashed border-subtle rounded-xl px-9 py-12 text-center min-h-[300px] flex flex-col items-center justify-center"
              style={{
                backgroundImage:
                  'radial-gradient(ellipse 70% 80% at 50% 100%, rgba(217,242,106,0.05) 0%, transparent 60%)',
              }}
            >
              <div className="w-12 h-12 rounded-xl bg-accent/[0.08] border border-accent/25 text-accent flex items-center justify-center mb-4.5">
                <span className="i-ph:sparkle text-base" />
              </div>
              <h3 className="font-instrument text-[28px] leading-tight mb-2">A blank page, on purpose.</h3>
              <p className="text-secondary text-[13.5px] max-w-[380px] mx-auto mb-4.5 leading-[1.6]">
                Start by describing the site you want.
              </p>
              <button onClick={() => setShowNewDialog(true)} className="ui-btn-accent">
                <span className="i-ph:plus text-sm" />
                New project
              </button>
            </div>
          ) : (
            <div className="text-center py-20 text-secondary">No matching projects</div>
          )
        ) : (
          <ProjectList
            projects={filteredProjects}
            onDelete={handleDelete}
            onClick={handleClick}
            onRename={handleRename}
            onNew={() => setShowNewDialog(true)}
            view={view}
          />
        )}
      </main>

      <NewProjectDialog
        open={showNewDialog}
        onOpenChange={setShowNewDialog}
        onCreated={(project) => {
          queryClient.invalidateQueries({ queryKey: ['projects'] });
          navigate(`/project/${project.id}`);
        }}
      />

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete this project?"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? Chats in this project will not be deleted, but will be unlinked.`}
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
